/** Tiny dedicated API helpers for the work-connector settings UI.
 *  Kept here (rather than in src/lib/api.ts) so the Settings panel can
 *  evolve without touching the shared api module. CSRF token comes
 *  from the existing auth lib. */
import { getCsrfToken } from "../../../lib/auth";

function csrfHeaders(): Record<string, string> {
  const t = getCsrfToken();
  return t ? { "X-CSRF-Token": t } : {};
}

export interface WorkConnectorRow {
  id: string;
  kind: string;
  displayName: string;
  enabled: boolean;
  config: Record<string, unknown>;
  defaultConfig: Record<string, unknown>;
  last_run_id: string | null;
  last_run_at: string | null;
  last_run_status: string | null;
}

export interface WorkRunRow {
  id: string;
  kind: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  items_captured: number;
  items_skipped: number;
  read_state_preserved: boolean | null;
  duration_ms: number | null;
  errors: { item?: string; error: string }[] | null;
  notes: string | null;
}

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, { credentials: "same-origin", ...init });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    try {
      const parsed = JSON.parse(text);
      throw new Error(String(parsed.error || res.statusText));
    } catch {
      throw new Error(text || `Request failed (${res.status})`);
    }
  }
  return (await res.json()) as T;
}

export const workConnectorsApi = {
  list(): Promise<{ connectors: WorkConnectorRow[] }> {
    return jsonFetch(`/api/work-connectors`);
  },
  update(kind: string, body: { enabled?: boolean; config?: Record<string, unknown> }): Promise<{ ok: boolean }> {
    return jsonFetch(`/api/work-connectors/${encodeURIComponent(kind)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      body: JSON.stringify(body),
    });
  },
  rotateSecret(kind: string): Promise<{ ok: boolean; rotatedAt: string }> {
    return jsonFetch(`/api/work-connectors/${encodeURIComponent(kind)}/rotate-secret`, {
      method: "POST",
      headers: csrfHeaders(),
    });
  },
  runNow(kind: string): Promise<{
    runId: string;
    instructionsPath: string;
    hmacSecret: string;
    bridgeUrl: string;
    window: { start: string; end: string };
  }> {
    return jsonFetch(`/api/work-connectors/${encodeURIComponent(kind)}/run`, {
      method: "POST",
      headers: csrfHeaders(),
    });
  },
  runs(kind: string): Promise<{ runs: WorkRunRow[] }> {
    return jsonFetch(`/api/work-connectors/${encodeURIComponent(kind)}/runs`);
  },
  verifyReadState(kind: string): Promise<{
    status: "preserved" | "violated" | "no-run-yet";
    auditAt?: string;
    runStatus?: string;
    audit?: Record<string, { before: string; after: string; restored?: boolean }>;
    message?: string;
  }> {
    return jsonFetch(`/api/work-connectors/${encodeURIComponent(kind)}/verify-read-state`);
  },
};

export interface OpenThreadPerson {
  id: string;
  display_name: string | null;
  role: string | null;
}

export interface OpenThreadEngagement {
  engagement_id: string;
  title: string;
  kind: string;
  scope: string | null;
  last_activity_at: string | null;
  direction_rank: number;
  activity_count: number;
  has_signal: boolean;
  people: OpenThreadPerson[];
}

export interface OpenThreadsResponse {
  threads: OpenThreadEngagement[];
  meta: {
    max_age_days: number;
    cutoff_iso: string;
    cap: number;
    total_candidates: number;
    stripped_by_recency: number;
    stripped_by_sensitivity: number;
    returned: number;
  };
}

export const briefApi = {
  latest(): Promise<{ brief: BriefPayload | null }> {
    return jsonFetch(`/api/brief/latest`);
  },
  assemble(): Promise<{ briefId: string; generatedAt: string; modelUsed: string; durationMs: number; sections: BriefSections }> {
    return jsonFetch(`/api/brief/assemble`, { method: "POST", headers: csrfHeaders() });
  },
  openThreads(): Promise<OpenThreadsResponse> {
    return jsonFetch(`/api/today/open-threads`);
  },
};

export interface TodayCalendarEvent {
  event_id: string;
  title: string;
  start: string | null;
  end: string | null;
  is_all_day: boolean;
  location: string | null;
  attendees: string[];
  organizer: string | null;
  scope: string | null;
  source_packet_id: string | null;
}

export const todayCalendarApi = {
  list(): Promise<{ window: { from: string; to: string }; events: TodayCalendarEvent[] }> {
    return jsonFetch(`/api/today/calendar`);
  },
};

export type BriefCalendarSubKind = "meeting" | "reminder" | "block" | "ooo";

export interface BriefSections {
  today_calendar: { source_id: string; title: string; start: string; end: string; attendees: string[]; location?: string; subKind?: BriefCalendarSubKind }[];
  open_threads: { source_id: string; source: string; subject: string; from?: string; channel?: string; preview: string; captured_at: string }[];
  decisions: { source_id: string; source: string; text: string; captured_at: string }[];
  work_updates: { source_id: string; source: string; text: string; captured_at: string }[];
  personal: { source_id: string; source: string; text: string; captured_at: string }[];
  context_now?: string;
  summary: string;
}

export interface BriefPayload {
  id: string;
  generated_at: string;
  scope_window: string;
  sections: BriefSections;
  source_ids: string[];
  model_used: string | null;
  duration_ms: number | null;
  /** W076.1 outbound delivery spine: headline, body_md and
   *  delivered_at let the Today hero render the brief that reached
   *  the user's device with a "Delivered at HH:MM" proof line. Older
   *  briefs may be missing headline/body_md if the synthesis failed;
   *  delivered_at is null until the delivery chokepoint stamps it. */
  headline?: string | null;
  body_md?: string | null;
  delivered_at?: string | null;
}
